/**
 * src/pages/AssistantAttendance.tsx
 * アシスタント自分専用：勤怠入力ページ
 *
 * - 自分の出勤記録だけ表示・登録・削除できる
 * - 締め済み月は閲覧のみ（登録・削除ボタンを非表示）
 * - payroll_enabled = 0 のアカウントはサーバー側で 403 を返す
 */

import { useEffect, useState, useCallback } from 'react'
import { api } from '../api/client'
import { todayJst } from '../utils/jst'
import { useAssistantMonth } from '../hooks/useAssistantMonth'
import { useAuth } from '../components/AuthGuard'
import { useIsMobile } from '../hooks/useIsMobile'


/** YYYY-MM を delta ヶ月ずらす */
function shiftMonth(m: string, delta: number): string {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(y, mo - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function currentLocalMonth(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 7)
}

/** YYYY-MM → "2026年5月" 形式 */
function monthLabel(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  return `${y}年${mo}月`
}

function fmt(n: number) { return n.toLocaleString('ja-JP') }

function diffMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  if ([sh, sm, eh, em].some((v) => Number.isNaN(v))) return 0
  const d = (eh * 60 + em) - (sh * 60 + sm)
  return Math.max(0, d)
}

const NORMAL_PAID_MINUTES = 510
const BREAK_MINUTES = 90        // フルタイム勤務の休憩時間デフォルト（未指定時）
const BREAK_MINUTES_MAX = 240   // 休憩時間の手動変更時の上限（異常値防止）

function calcPartTimeMinutesByTimes(checkIn: string, checkOut: string, breakMinutes = 0): number {
  const clock = diffMinutes(checkIn, checkOut)
  return Math.round(Math.max(0, clock - Math.max(0, Number(breakMinutes) || 0)))
}

// フルタイム勤務の残業分数。「実働時間が通常勤務時間(510分)を超えた分」で算出する
// （退勤時刻が20:00を超えたかどうかでは判定しない）。保存時はサーバー側でも同じ考え方で再計算される。
// breakMinutes は通常90分固定だが、必要な場合のみ手動変更できる。
function calcFullTimeOvertimeMinutes(checkIn: string, checkOut: string, breakMinutes = BREAK_MINUTES): number {
  const totalMin = diffMinutes(checkIn, checkOut) - breakMinutes
  return Math.max(0, totalMin - NORMAL_PAID_MINUTES)
}

/** 休憩時間分数を "1.5時間" / "60分" のように表示する（90分の時だけ時間表記） */
function formatBreakMinutes(minutes: number): string {
  return minutes === BREAK_MINUTES ? '1.5時間' : `${minutes}分`
}

function normalizeFormForSubmit(form: FormValues) {
  if (form.work_type !== 'part_time') {
    const breakMinutes = Math.min(BREAK_MINUTES_MAX, Math.max(0, Math.round(Number(form.break_minutes) || 0)))
    return {
      workMinutes: 0,
      overtimeMin: calcFullTimeOvertimeMinutes(form.check_in, form.check_out, breakMinutes),
      breakMinutes,
    }
  }

  const breakMinutes = Math.max(0, Number(form.break_minutes) || 0)
  const manualMinutes = Math.max(0, Number(form.work_minutes) || 0)
  const computedMinutes = calcPartTimeMinutesByTimes(form.check_in, form.check_out, breakMinutes)

  return {
    workMinutes: manualMinutes > 0 ? manualMinutes : computedMinutes,
    overtimeMin: 0, // 時間勤務・時短勤務では残業概念を持たない（二重計算防止）
    breakMinutes,
  }
}

// ================================================================
// 勤怠入力モーダル（登録・上書き両用）
// ================================================================
type FormValues = {
  work_date:     string
  check_in:      string
  check_out:     string
  work_type:     'full_time' | 'part_time'
  work_minutes:  string   // 時間勤務・時短勤務の勤務時間（分）
  break_minutes: string   // 時間勤務・時短勤務の休憩時間（分）
  overtime_min:  string   // 文字列で保持（分単位）
  transport_fee: string
  meal_provided: string  // '1' | '0'
  notes:         string
}

function emptyForm(defaultDate: string, defaultTransportFee = 0): FormValues {
  return {
    work_date:     defaultDate,
    check_in:      '10:00',
    check_out:     '20:00',
    work_type:     'full_time',
    work_minutes:  '0',
    break_minutes: String(BREAK_MINUTES),
    overtime_min:  '0',
    transport_fee: String(defaultTransportFee),
    meal_provided: '1',
    notes:         '',
  }
}

function AttendanceFormModal({
  defaultDate,
  initial,
  editId,
  isClosed,
  onClose,
  onSaved,
  onDeleted,
  defaultTransportFee = 0,
}: {
  defaultDate: string
  initial?: FormValues
  editId?: string          // 編集中レコードのID（削除用）
  isClosed: boolean
  onClose: () => void
  onSaved: (id: string, date: string) => void
  onDeleted?: (date: string) => void
  defaultTransportFee?: number
}) {
  const [form,           setForm]           = useState<FormValues>(initial ?? emptyForm(defaultDate, defaultTransportFee))
  const [saving,         setSaving]         = useState(false)
  const [deleting,       setDeleting]       = useState(false)
  const [confirmDelete,  setConfirmDelete]  = useState(false)
  const [deleteError,    setDeleteError]    = useState<string | null>(null)
  const [error,          setError]          = useState<string | null>(null)
  const [isEditingBreak, setIsEditingBreak] = useState(false)

  const set = (k: keyof FormValues, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setError(null)
  }

  const recalcPartTimeMinutes = (breakMinutesValue?: string) => {
    const breakMin = Number(breakMinutesValue ?? form.break_minutes) || 0
    const minutes = calcPartTimeMinutesByTimes(form.check_in, form.check_out, breakMin)
    setForm((f) => ({
      ...f,
      work_minutes: String(minutes),
      break_minutes: String(breakMin),
      overtime_min: '0',
    }))
    setError(null)
  }

  // ★ 出勤時刻・退勤時刻・勤務区分の変更時に、残業時間（または勤務時間）を自動再計算する。
  // 手動の「残業自動計算」ボタンは廃止し、自動計算に一本化した。
  // （保存時はサーバー側でも同じ考え方で必ず再計算されるため、二重に安全）
  const recalcDerivedFields = (next: FormValues): FormValues => {
    if (next.work_type === 'part_time') {
      const breakMin = Number(next.break_minutes) || 0
      const minutes = calcPartTimeMinutesByTimes(next.check_in, next.check_out, breakMin)
      return { ...next, work_minutes: String(minutes), overtime_min: '0' }
    }
    const fullTimeBreakMin = Number(next.break_minutes) || 0
    return { ...next, overtime_min: String(calcFullTimeOvertimeMinutes(next.check_in, next.check_out, fullTimeBreakMin)) }
  }

  const updateTime = (key: 'check_in' | 'check_out', value: string) => {
    setForm((f) => recalcDerivedFields({ ...f, [key]: value }))
    setError(null)
  }

  // フルタイム勤務の休憩時間を手動変更した時の再計算（0〜240分にクランプ）
  const updateFullTimeBreakMinutes = (value: string) => {
    setForm((f) => recalcDerivedFields({ ...f, break_minutes: value }))
    setError(null)
  }

  const updateWorkType = (value: 'full_time' | 'part_time') => {
    setForm((f) => recalcDerivedFields({ ...f, work_type: value }))
    setError(null)
  }

  const handleSave = async () => {
    if (!form.work_date) { setError('出勤日を入力してください'); return }
    if (form.work_type !== 'part_time') {
      const breakVal = form.break_minutes.trim()
      const breakNum = Number(breakVal)
      if (breakVal === '' || !Number.isFinite(breakNum) || breakNum < 0 || breakNum > BREAK_MINUTES_MAX) {
        setError(`休憩時間は0〜${BREAK_MINUTES_MAX}分の範囲で入力してください`)
        return
      }
    }
    setSaving(true); setError(null)
    try {
      const normalized = normalizeFormForSubmit(form)
      const result = await api.my.attendance.record({
        work_date:    form.work_date,
        check_in:     form.check_in  || '10:00',
        check_out:    form.check_out || '20:00',
        overtime_min: normalized.overtimeMin,
        break_minutes: normalized.breakMinutes,
        transport_fee: Number(form.transport_fee) || 0,
        meal_provided: Number(form.meal_provided),
        work_type:    form.work_type,
        work_minutes: normalized.workMinutes,
        notes:        form.notes || undefined,
      })
      onSaved(result.id ?? '', form.work_date)
    } catch (ex: any) {
      setError(ex.message ?? 'エラーが発生しました')
    } finally { setSaving(false) }
  }

  const inputStyle: React.CSSProperties = { fontSize: 16 }

  return (
    <div className="overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">
            {initial ? '勤怠を修正' : '勤怠を入力'}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {isClosed && (
            <div style={{
              background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
              borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: 'var(--amber)',
            }}>
              ⚠ この月は締め済みのため編集できません
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>出勤日 <span style={{ color: 'var(--red)' }}>*</span></label>
            <input type="date" value={form.work_date}
              onChange={(e) => set('work_date', e.target.value)}
              disabled={isClosed || saving} style={inputStyle} />
          </div>

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>勤務区分</label>
            <select value={form.work_type}
              onChange={(e) => updateWorkType(e.target.value as 'full_time' | 'part_time')}
              disabled={isClosed || saving} style={inputStyle}>
              <option value="full_time">フルタイム（日給）</option>
              <option value="part_time">時間勤務・時短勤務</option>
            </select>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
              早退・時短・時間勤務の場合は「時間勤務」を選び、勤務時間を分で入力してください。
            </div>
          </div>

          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>出勤時刻</label>
              <input type="time" value={form.check_in}
                onChange={(e) => updateTime('check_in', e.target.value)}
                disabled={isClosed || saving} style={inputStyle} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>退勤時刻</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="time" value={form.check_out}
                  onChange={(e) => updateTime('check_out', e.target.value)}
                  disabled={isClosed || saving} style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>
          </div>

          {form.work_type === 'part_time' && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>時間勤務・時短勤務の勤務時間（分）</label>
              <input type="number" inputMode="numeric" min="0" step="30"
                value={form.work_minutes}
                onChange={(e) => set('work_minutes', e.target.value)}
                disabled={isClosed || saving} style={inputStyle}
                placeholder="例: 210" />
              <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                <label>休憩時間（分）</label>
                <select value={form.break_minutes}
                  onChange={(e) => recalcPartTimeMinutes(e.target.value)}
                  disabled={isClosed || saving} style={inputStyle}>
                  <option value="0">休憩なし</option>
                  <option value="30">30分</option>
                  <option value="60">昼休憩あり（60分）</option>
                  <option value="90">90分</option>
                </select>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  出退勤時刻と休憩時間から勤務時間を自動計算します。時間勤務・時短勤務では残業は計上しません。
                </div>
              </div>
              {Number(form.work_minutes) > 0 && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  ≈ {(Number(form.work_minutes) / 60).toFixed(1)}時間
                </div>
              )}
            </div>
          )}

          {form.work_type === 'part_time' ? (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>交通費（円）</label>
              <input type="number" inputMode="numeric" min="0"
                value={form.transport_fee}
                onChange={(e) => set('transport_fee', e.target.value)}
                disabled={isClosed || saving} style={inputStyle} />
              {defaultTransportFee > 0 && !initial && (
                <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 2 }}>
                  通常の交通費（日額）：¥{defaultTransportFee.toLocaleString('ja-JP')} を自動入力しました
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                時間勤務・時短勤務は勤務時間のみで計算します。
              </div>
            </div>
          ) : (
            <>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>休憩時間</label>
              {isEditingBreak ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" inputMode="numeric" min={0} max={BREAK_MINUTES_MAX} step={1}
                    value={form.break_minutes}
                    onChange={(e) => updateFullTimeBreakMinutes(e.target.value)}
                    disabled={isClosed || saving} style={{ ...inputStyle, width: 90 }} />
                  <span style={{ fontSize: 13 }}>分</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>
                    {formatBreakMinutes(Number(form.break_minutes) || 0)}
                  </span>
                  {!isClosed && (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                      onClick={() => setIsEditingBreak(true)}>休憩時間を変更</button>
                  )}
                </div>
              )}
            </div>
            <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>残業時間</label>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{form.overtime_min}分</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                実勤務 {(Math.max(0, diffMinutes(form.check_in, form.check_out) - (Number(form.break_minutes) || 0)) / 60).toFixed(1)}時間
                （残業は実勤務が{(NORMAL_PAID_MINUTES / 60).toFixed(1)}時間を超えた分を自動計算）
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>交通費（円）</label>
              <input type="number" inputMode="numeric" min="0"
                value={form.transport_fee}
                onChange={(e) => set('transport_fee', e.target.value)}
                disabled={isClosed || saving} style={inputStyle} />
              {defaultTransportFee > 0 && !initial && (
                <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 2 }}>
                  通常：¥{defaultTransportFee.toLocaleString('ja-JP')}
                </div>
              )}
            </div>
          </div>
          </>
          )}

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>食事支給</label>
            <select value={form.meal_provided}
              onChange={(e) => set('meal_provided', e.target.value)}
              disabled={isClosed || saving} style={inputStyle}>
              <option value="1">あり</option>
              <option value="0">なし</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>備考</label>
            <input type="text" value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              disabled={isClosed || saving} style={inputStyle}
              placeholder="特記事項があれば" />
          </div>

          {error && (
            <div style={{
              color: 'var(--red)', fontSize: 12, marginTop: 12,
              padding: '8px 12px', background: '#fde8e6', borderRadius: 6,
            }}>
              ⚠ {error}
            </div>
          )}

          {/* 削除セクション（既存の記録を修正中のときのみ表示） */}
          {editId && !isClosed && (
            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: '1px solid var(--paper-3)',
            }}>
              {!confirmDelete ? (
                <button
                  type="button"
                  onClick={() => { setDeleteError(null); setConfirmDelete(true) }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--ink-3)', fontSize: 12, padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  この勤怠入力を削除
                </button>
              ) : (
                <div style={{
                  background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
                  borderRadius: 8, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    この勤怠入力を削除します。
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10, lineHeight: 1.6 }}>
                    よろしいですか？
                  </div>
                  {deleteError && (
                    <div style={{
                      fontSize: 12, color: 'var(--red)', background: '#fde8e6',
                      borderRadius: 6, padding: '8px 10px', marginBottom: 10,
                    }}>
                      ⚠ {deleteError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: 'var(--red)', color: '#fff',
                        border: 'none', cursor: 'pointer',
                      }}
                      disabled={deleting}
                      onClick={async () => {
                        setDeleting(true); setDeleteError(null)
                        try {
                          await api.my.attendance.delete(editId)
                          onDeleted?.(form.work_date)
                          onClose()
                        } catch (ex: any) {
                          // 給与申告済みの場合は専用メッセージ
                          if (ex.message?.includes('給与申告') || ex.message?.includes('payroll')) {
                            setDeleteError(ex.message)
                          } else {
                            setDeleteError(ex.message ?? '削除に失敗しました')
                          }
                          setConfirmDelete(false)
                        } finally { setDeleting(false) }
                      }}
                    >
                      {deleting ? '削除中...' : '削除する'}
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => { setConfirmDelete(false); setDeleteError(null) }}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave}
            disabled={saving || isClosed}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// Undo トースト
// ================================================================
function UndoBanner({ msg, onUndo, onClose }: { msg: string; onUndo: () => void; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000)
    return () => clearTimeout(t)
  }, [onClose])

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--paper)', border: '1px solid var(--paper-3)',
      borderRadius: 8, padding: '12px 18px', zIndex: 200, minWidth: 280,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <span style={{ fontSize: 13, flex: 1 }}>{msg}</span>
      <button className="btn btn-outline btn-sm" onClick={onUndo}>元に戻す</button>
      <button onClick={onClose}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 16, color: 'var(--ink-3)', padding: 0 }}>✕</button>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function AssistantAttendance() {
  const { user }   = useAuth()
  const isMobile   = useIsMobile()

  const [month, setMonth]      = useAssistantMonth(user?.assistant_id)
  const [records,            setRecords]         = useState<any[]>([])
  const [isClosed,           setIsClosed]        = useState(false)
  const [loading,            setLoading]         = useState(true)
  const [showForm,           setShowForm]        = useState(false)
  const [editRec,            setEditRec]         = useState<any | null>(null)
  const [undoRec,            setUndoRec]         = useState<{ date: string; deleted: any } | null>(null)
  const [accessError,        setAccessError]     = useState<string | null>(null)
  const [defaultTransport,   setDefaultTransport] = useState(0)
  const load = useCallback(async () => {
    setLoading(true)
    setAccessError(null)
    try {
      const [recs, closeStatus, profile] = await Promise.all([
        api.my.attendance.list(month),
        api.my.closes.get(month),
        api.my.profile.get().catch(() => null),
      ])
      setRecords(recs)
      setIsClosed(closeStatus.closed)
      if (profile) setDefaultTransport(profile.default_transportation_fee ?? 0)
    } catch (ex: any) {
      if (ex.message?.includes('Forbidden') || ex.message?.includes('対象外')) {
        setAccessError(ex.message)
      }
    } finally { setLoading(false) }
  }, [month])

  useEffect(() => { load() }, [load])

  // 合計
  const totalDays = records.length
  const totalTransport = records.reduce((s, r) => s + (r.transport_fee ?? 0), 0)
  const totalOvertime  = records.reduce((s, r) => s + (r.overtime_min ?? 0), 0)

  const handleUndo = async () => {
    if (!undoRec?.deleted) {
      setUndoRec(null)
      return
    }
    try {
      await api.my.attendance.record({
        work_date:     undoRec.deleted.work_date,
        check_in:      undoRec.deleted.check_in,
        check_out:     undoRec.deleted.check_out,
        overtime_min:  undoRec.deleted.overtime_min ?? 0,
        transport_fee: undoRec.deleted.transport_fee ?? 0,
        meal_provided: undoRec.deleted.meal_provided ?? 1,
        work_type:     undoRec.deleted.work_type === 'part_time' ? 'part_time' : 'full_time',
        work_minutes:  undoRec.deleted.work_minutes ?? 0,
        break_minutes: undoRec.deleted.break_minutes ?? 0,
        notes:         undoRec.deleted.notes ?? undefined,
      })
      setUndoRec(null)
      await load()
    } catch (ex: any) {
      alert(ex.message ?? '復元に失敗しました')
    }
  }

  if (accessError) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>🚫</div>
        <div style={{ fontSize: 14 }}>{accessError}</div>
      </div>
    )
  }

  return (
    <div>
      {/* 操作バー：月ナビ + 主操作ボタン */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 16,
        flexWrap: 'wrap',
      }}>
        {/* 左：タイトル + 締め済みバッジ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>勤怠一覧</span>
          <span style={{
            fontSize: 12, color: 'var(--ink-3)',
            background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
            borderRadius: 4, padding: '1px 8px',
          }}>
            {monthLabel(month)}
          </span>
          {isClosed && (
            <span style={{
              fontSize: 11, color: 'var(--amber)', background: '#fef3dc',
              border: '1px solid rgba(176,122,18,0.25)', borderRadius: 4, padding: '2px 8px',
            }}>
              🔒 締め済み
            </span>
          )}
        </div>

        {/* 右：月ナビ + 主操作ボタン */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
        }}>
          <button type="button" className="btn btn-outline btn-sm"
            onClick={() => setMonth(shiftMonth(month, -1))}>
            前月
          </button>
          <input type="month" value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 13, width: 134 }} />
          <button type="button" className="btn btn-outline btn-sm"
            onClick={() => setMonth(shiftMonth(month, 1))}>
            翌月
          </button>
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => setMonth(currentLocalMonth())}>
            今月
          </button>
          {!isClosed && !loading && (
            <button className="btn btn-primary btn-sm"
              onClick={() => { setEditRec(null); setShowForm(true) }}
              style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 40 : undefined }}>
              ＋ 勤怠を入力
            </button>
          )}
        </div>
      </div>

      {/* サマリー */}
      {!loading && records.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: '出勤日数', value: `${totalDays}日` },
            { label: '交通費合計', value: `¥${fmt(totalTransport)}` },
            { label: '残業合計', value: `${(totalOvertime / 60).toFixed(1)}h` },
          ].map((m) => (
            <div key={m.label} className="metric" style={{ minWidth: 110, padding: '10px 14px' }}>
              <div className="metric-label">{m.label}</div>
              <div className="metric-value" style={{ fontSize: 16 }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 一覧 */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">勤怠一覧</span>
          {user?.assistant_name && (
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {user.assistant_name}
            </span>
          )}
        </div>

        {loading ? (
          <div className="loading" style={{ padding: 32 }}>LOADING...</div>
        ) : records.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)', fontSize: 13 }}>
            {isClosed ? 'この月の勤怠記録はありません' : '勤怠が登録されていません。「＋ 勤怠を入力」から追加してください。'}
          </div>
        ) : isMobile ? (
          // モバイル：カード表示
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
            {records.map((r) => (
              <div key={r.id} style={{
                background: 'var(--paper-2)', borderRadius: 8,
                padding: '12px 14px', border: '1px solid var(--paper-3)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700 }}>
                    {r.work_date}
                  </span>
                  {!isClosed && (
                    <button className="btn btn-outline btn-sm"
                      onClick={() => { setEditRec(r); setShowForm(true) }}
                      style={{ fontSize: 11 }}>修正</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap', fontSize: 12 }}>
                  <span>{r.check_in} – {r.check_out}</span>
                  <span>{r.work_type === 'part_time'
                    ? `時間勤務 ${(r.work_minutes ?? 0)}分${(r as any).break_minutes ? `（休憩${(r as any).break_minutes}分）` : ''}`
                    : `フルタイム${(r as any).break_minutes && (r as any).break_minutes !== BREAK_MINUTES ? `（休憩${(r as any).break_minutes}分）` : ''}`}</span>
                  {r.overtime_min > 0 && <span style={{ color: 'var(--amber)' }}>残業 {r.overtime_min}分</span>}
                  {r.transport_fee > 0 && <span>交通費 ¥{fmt(r.transport_fee)}</span>}
                </div>
                {r.notes && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{r.notes}</div>}
              </div>
            ))}
          </div>
        ) : (
          // PC：テーブル表示
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>出勤日</th><th>区分</th><th>出勤</th><th>退勤</th>
                  <th>残業(分)</th><th>交通費</th><th>食事</th>
                  <th>備考</th>
                  {!isClosed && <th></th>}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{r.work_date}</td>
                    <td style={{ fontSize: 11 }}>
                      {r.work_type === 'part_time'
                        ? <span className="badge badge-blue">時間 {r.work_minutes ?? 0}分{(r as any).break_minutes ? ` / 休憩${(r as any).break_minutes}分` : ''}</span>
                        : <span className="badge badge-gray">フル{(r as any).break_minutes && (r as any).break_minutes !== BREAK_MINUTES ? ` / 休憩${(r as any).break_minutes}分` : ''}</span>}
                    </td>
                    <td className="mono">{r.check_in}</td>
                    <td className="mono">{r.check_out}</td>
                    <td className="mono" style={{ color: r.overtime_min > 0 ? 'var(--amber)' : 'var(--ink-3)' }}>
                      {r.overtime_min > 0 ? r.overtime_min : '—'}
                    </td>
                    <td className="mono">
                      {r.transport_fee > 0 ? `¥${fmt(r.transport_fee)}` : '—'}
                    </td>
                    <td style={{ fontSize: 11 }}>{r.meal_provided ? 'あり' : 'なし'}</td>
                    <td style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 140,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.notes || '—'}
                    </td>
                    {!isClosed && (
                      <td>
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => { setEditRec(r); setShowForm(true) }}
                          style={{ fontSize: 11 }}>修正</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 勤怠入力モーダル */}
      {showForm && (
        <AttendanceFormModal
          defaultDate={todayJst()}
          isClosed={isClosed}
          defaultTransportFee={defaultTransport}
          editId={editRec?.id ?? undefined}
          initial={editRec ? {
            work_date:    editRec.work_date,
            check_in:     editRec.check_in     ?? '10:00',
            check_out:    editRec.check_out    ?? '20:00',
            work_type:    editRec.work_type === 'part_time' ? 'part_time' : 'full_time',
            work_minutes: String(editRec.work_minutes ?? 0),
            break_minutes: String((editRec as any).break_minutes ?? BREAK_MINUTES),
            overtime_min:  String(editRec.overtime_min  ?? 0),
            transport_fee: String(editRec.transport_fee ?? 0),
            meal_provided: String(editRec.meal_provided ?? 1),
            notes:         editRec.notes ?? '',
          } : undefined}
          onClose={() => { setShowForm(false); setEditRec(null) }}
          onSaved={async (id, date) => {
            setShowForm(false); setEditRec(null)
            if (id) setUndoRec(null)
            await load()
            void date
          }}
          onDeleted={(date) => {
            setShowForm(false); setEditRec(null)
            setUndoRec(null)
            load()
            // 削除完了のフィードバックはUndoBannerは使わず、一覧更新で代替
            // （soft deleteのためundo復元機能はスキップ）
            void date
          }}
        />
      )}

      {/* Undo トースト（削除後） */}
      {undoRec && (
        <UndoBanner
          msg={`${undoRec.date} の勤怠を削除しました`}
          onUndo={handleUndo}
          onClose={() => setUndoRec(null)}
        />
      )}
    </div>
  )
}